import React from 'react';
import styled from 'styled-components';

import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import {
  AdminPortalSystem,
  adminPortalSystemQueryKeys,
  indexAdminPortalSystems,
} from 'services/api/admin/portalSystems';
import PencilIcon from 'svg/PencilIcon';

// Admin → Portal Systems (OLIO-3074): the portal registry, listing each system's
// collected-field configuration. Collection is global per portal system — every client
// using the portal gets the same contract — so this lives outside the per-client pages.
const config: AdminListPageConfig<AdminPortalSystem> = {
  title: 'Portal Systems',
  columnStorageKey: 'admin.portalSystems',
  queryKey: adminPortalSystemQueryKeys.index,
  fetchFn: indexAdminPortalSystems,
  fetchParams: { defaultSort: 'displayName asc' },
  showResultCount: true,

  columns: [
    {
      id: 'displayName',
      header: 'Portal',
      render: (row) => <BoldText>{row.displayName}</BoldText>,
    },
    { id: 'systemName', header: 'System Name' },
    {
      id: 'primaryId',
      header: 'Primary ID',
      sortable: false,
      render: (row) => primaryIdLabel(row),
    },
    {
      id: 'retrievalFields',
      header: 'Collected on Retrieval',
      sortable: false,
      render: (row) => fieldCount(row.retrievalFields.filter((field) => field.enabled).length),
    },
    {
      id: 'scanFields',
      header: 'Collected on Scans',
      sortable: false,
      render: (row) => fieldCount(row.scanFields.filter((field) => field.enabled).length),
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminClientEdit',
      onClick: (row, { navigate }) => navigate(`/admin/portal-systems/${row.id}/edit`),
    },
  ],
};

export function primaryIdLabel(system: AdminPortalSystem, name = system.primaryId) {
  if (name === 'url') return 'URL';

  return system.retrievalFields.find((field) => field.name === name)?.label ?? name;
}

function fieldCount(count: number) {
  return `${count} ${count === 1 ? 'field' : 'fields'}`;
}

export default function PortalSystems() {
  const state = useAdminListPage(config);
  return <AdminListPageRenderer config={config} state={state} />;
}

const BoldText = styled.span`
  font-weight: 500;
`;
